import 'twin.macro';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import _partition from '@teetsh/app/src/utils/arrays/partition';
import { replaceItemAtIndex } from '@teetsh/app/src/utils/listMethods';
import { getNoteRange } from './NotationsLabel/getRangeLabel';
import Input from '@teetsh/app/src/components/Form/InputGroup';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { NotationsLabel } from './NotationsLabel';
import { MultiSlider } from './MultiSlider';
import { RangeVisualization } from './RangeVisualization';
import DeleteButton from 'components/Buttons/DeleteButton';
import { NotationSystemData } from 'containers/UserSettings/NotationSystems/types';
import { ExampleCallout } from 'containers/UserSettings/NotationSystems/ExampleCallout';
import Select from 'components/Form/Select2';
import NumberInput from 'components/Form/NumberInput';
import FormGroup from 'components/Form/FormGroup';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  notationSystem: NotationSystemData;
  index: number;
  allNotationSystems: NotationSystemData[];
  onChange: (value: NotationSystemData[]) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export function NotationSystem({
  notationSystem,
  index,
  allNotationSystems,
  onChange,
  onDelete,
  onDuplicate,
}: Props) {
  const { t } = useTranslation(['userSettings', 'common']);
  const [changegeableValues, unchangeableValues] = _partition(
    notationSystem.values,
    v => v.value >= 0,
  );

  return (
    <div tw="bg-neutral-supershy rounded-lg p-4 relative">
      <div tw="mb-3">
        <Input
          id={notationSystem.id}
          label={t('userSettings:notationSystems.name')}
          value={notationSystem.name}
          onChange={name =>
            onChange(
              replaceItemAtIndex(
                allNotationSystems,
                { ...notationSystem, name },
                index,
              ),
            )
          }
        />
      </div>
      <div tw="mb-3">
        <Select
          tw="bg-white"
          label={t('userSettings:notationSystems.typeLabel')}
          value={notationSystem.type || 'competency'}
          options={[
            {
              value: 'numerical',
              label: t('userSettings:notationSystems.typeNumerical'),
            },
            {
              value: 'competency',
              label: t('userSettings:notationSystems.typeCompetency'),
            },
          ]}
          onChange={type =>
            onChange(
              replaceItemAtIndex(
                allNotationSystems,
                { ...notationSystem, type },
                index,
              ),
            )
          }
        />
      </div>
      <div tw="mb-8">
        {notationSystem.type === 'numerical' ? (
          <>
            <div tw="flex flex-col md:flex-row gap-3 mb-3">
              <div tw="flex-1">
                <FormGroup
                  withoutMargin
                  label={t('userSettings:notationSystems.minValue')}
                >
                  <NumberInput
                    disabled
                    value={notationSystem.minValue ?? 0}
                    placeholder={t(
                      'userSettings:notationSystems.minValuePlaceholder',
                    )}
                    min={0}
                    onChange={minValue =>
                      onChange(
                        replaceItemAtIndex(
                          allNotationSystems,
                          { ...notationSystem, minValue },
                          index,
                        ),
                      )
                    }
                  />
                </FormGroup>
              </div>
              <div tw="flex-1">
                <FormGroup
                  withoutMargin
                  label={
                    notationSystem.noMaxValue
                      ? t('userSettings:notationSystems.minForMaxValue')
                      : t('userSettings:notationSystems.maxValue')
                  }
                >
                  <NumberInput
                    value={notationSystem.maxValue ?? 100}
                    placeholder={t(
                      'userSettings:notationSystems.maxValuePlaceholder',
                    )}
                    disabled={false}
                    onChange={maxValue =>
                      onChange(
                        replaceItemAtIndex(
                          allNotationSystems,
                          {
                            ...notationSystem,
                            maxValue,
                          },
                          index,
                        ),
                      )
                    }
                  />
                </FormGroup>
              </div>
            </div>
            <div tw="flex flex-col gap-2">
              <Checkbox
                id={`${notationSystem.id}-no-max-value`}
                label={t('userSettings:notationSystems.noMaxValue')}
                checked={notationSystem.noMaxValue ?? false}
                onChange={e =>
                  onChange(
                    replaceItemAtIndex(
                      allNotationSystems,
                      { ...notationSystem, noMaxValue: e.target.checked },
                      index,
                    ),
                  )
                }
              />
            </div>
          </>
        ) : (
          <NotationsLabel
            onChange={values =>
              onChange(
                replaceItemAtIndex(
                  allNotationSystems,
                  {
                    ...notationSystem,
                    values,
                  },
                  index,
                ),
              )
            }
            notations={notationSystem.values}
          />
        )}
      </div>
      {notationSystem.type === 'competency' && (
        <div tw="mb-4 px-4 pt-2 pb-4 bg-neutral-quiet rounded-lg">
          <h5 tw="mb-4">
            {t('userSettings:notationSystems.equivalenceTitle')}
          </h5>
          <p tw="mb-4 text-sm">
            <Trans
              i18nKey="userSettings:notationSystems.equivalenceToHundred"
              values={{
                systemName: notationSystem.name,
              }}
            >
              AAA <strong>{'{{systemName}}'}</strong>BBB
            </Trans>
          </p>
          <div tw="pb-10 mb-4 px-2.5">
            <MultiSlider
              noColors
              handles={changegeableValues}
              onChange={values =>
                onChange(
                  replaceItemAtIndex(
                    allNotationSystems,
                    {
                      ...notationSystem,
                      values: [...unchangeableValues, ...values],
                    },
                    index,
                  ),
                )
              }
            />
          </div>
          <p tw="mb-4 text-sm">
            <Trans
              i18nKey="userSettings:notationSystems.equivalenceFromHundred"
              values={{
                systemName: notationSystem.name,
              }}
            >
              AAA <strong>{'{{systemName}}'}</strong>BBB
            </Trans>
          </p>
          <div tw="pb-10 px-2">
            <RangeVisualization
              handles={notationSystem.values.map((notationValue, idx) => {
                const range = getNoteRange(idx, notationSystem.values);
                return {
                  id: notationValue.id,
                  label: notationValue.label,
                  value: range?.end || notationValue.value,
                };
              })}
            />
          </div>
          <ExampleCallout notationSystem={notationSystem} />
        </div>
      )}

      <div tw="flex justify-end gap-2 mt-4">
        <DeleteButton
          withConfirmation
          onClick={onDelete}
          outlined
          icon={TrashIcon}
        >
          {t('common:buttons.delete')}
        </DeleteButton>
        <Button onClick={onDuplicate} outlined icon={DocumentDuplicateIcon}>
          {t('common:buttons.duplicate')}
        </Button>
      </div>
    </div>
  );
}
